import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, vShow as _vShow, withCtx as _withCtx, createBlock as _createBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode } from "vue"

import { reactive, ref, computed, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { ElMessage } from 'element-plus'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import ColumnSettingsDropdown from '@/components/column-settings-dropdown/index.vue'
import type { ColumnItem } from '@/components/column-settings-dropdown/index.vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'
import { useParseTime } from '@/hooks'
import { formatTimeStamp } from '@/utils/time'
import { listCol } from '@/utils/list-columns-i18n'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import {
  FormType,
  ObjectFormItem,
  DateType,
  FormSize,
} from '@/components/cmForm/types'
import type { TableOpt, TableActOpt } from '@/components/cmTable/types'
import dayjs from 'dayjs'
import {
  SecurityDisposeStatus,
  SecurityEventLevel,
  DEFAULT_PENDING_STATUS,
} from '../constants/enums'
import {
  attackTacticOptions,
  handleResultOptions,
  disposeStatusOptions,
  eventTypeOptions,
  levelOptions,
  listLevelColor,
  trAttackTactic,
  trHandleResultsSummary,
  trDisposeStatus,
  trListEventLevel,
  trEventDesc,
  trEventTypeDisplay,
  trListThreatType,
  disposeStatusTagType,
} from '../labels'
import { SecurityEventService, buildDefaultListQuery } from '../security-event.service'
import type { SecurityEventListItem, SecurityEventListQuery } from '../types'
import CancelIgnoreDialog from './dialogs/cancel-ignore-dialog.vue'
import CancelTrustDialog from './dialogs/cancel-trust-dialog.vue'
import MarkHandledDialog from './dialogs/mark-handled-dialog.vue'
import MarkIgnoredDialog from './dialogs/mark-ignored-dialog.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'security-event-list',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview,
  },
  hasPermission: { type: Boolean, default: false },
  initialHandleStatus: { type: Number as PropType<SecurityDisposeStatus | undefined>, default: undefined },
  initialEventLevel: { type: Number as PropType<SecurityEventLevel | undefined>, default: undefined },
  initialEventType: { type: String, default: undefined },
  initialAttackTactic: { type: String, default: undefined },
  initialTimeRangeUnix: {
    type: Array as unknown as PropType<[number, number] | undefined>,
    default: undefined,
  },
},
  emits: ["detail", "viewAll", "dispose"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const { t, locale } = useI18n()
const injector = useReflectiveInjector([SecurityEventService])
const securityEventService = injector.get(SecurityEventService)

function resolveInitialTime () {
  const unix = props.initialTimeRangeUnix
  if (unix?.length === 2 && unix[0] != null && unix[1] != null) {
    const date = [dayjs.unix(unix[0]).format('YYYY-MM-DD'), dayjs.unix(unix[1]).format('YYYY-MM-DD')] as [string, string]
    const [s, e] = useParseTime(date)
    return { date, start_time: s, end_time: e }
  }
  // 未指定时间范围时默认查询当天
  const today = dayjs().format('YYYY-MM-DD')
  const date = [today, today] as [string, string]
  const [s, e] = useParseTime(date)
  return { date, start_time: s, end_time: e }
}

function resolveDefaultHandleStatus () {
  if (props.initialHandleStatus != null) return props.initialHandleStatus
  if (props.viewMode === TableViewMode.preview) return DEFAULT_PENDING_STATUS
  return undefined
}

function handleViewAll () {
  emit('viewAll')
}

const timeInit = resolveInitialTime()
const defaultQuery = buildDefaultListQuery({
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  handle_status: resolveDefaultHandleStatus(),
  event_level: props.initialEventLevel,
  event_type: props.initialEventType,
  attack_tactic: props.initialAttackTactic,
  start_time: timeInit.start_time,
  end_time: timeInit.end_time,
})

const queryParams = reactive<SecurityEventListQuery & { date?: [string, string] }>({
  ...defaultQuery,
  group_id: -1,
  date: timeInit.date,
  keyword: '',
  handle_result:-1
})

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const selectedRows = ref<SecurityEventListItem[]>([])

// 定义所有列配置（包含默认显示状态）
const allColumnsConfig = computed(() => {
  void locale.value
  return [
    { key: 'event_level', prop: 'event_level',fixed:'left', label: t('securityEvent.colLevel'), minWidth: 96, slot: true, defaultVisible: true },
    { key: 'event_type', prop: 'event_type', label: t('securityEvent.colEventType'), minWidth: 140, slot: true, defaultVisible: true },
    { key: 'event_id', prop: 'event_id', label: t('securityEvent.colDescription'), minWidth: 200, slot: true, showOverflow: true, defaultVisible: true },
    { key: 'threat_entity', prop: 'threat_entity', label: t('securityEvent.colThreatEntity'), minWidth: 200, showOverflow: true, defaultVisible: true },
    // { key: 'threat_type', prop: 'threat_type', label: t('securityEvent.colThreatType'), minWidth: 100, slot: true, defaultVisible: true },
    { key: 'computer_name', prop: 'computer_name', label: listCol(t, 'computerName'), showOverflow: true, type: 'COMPUTER', minWidth: 160, defaultVisible: true },
    { key: 'group', prop: 'group_name', label: listCol(t, 'groupName'), minWidth: 160, defaultVisible: false },
    { key: 'username', prop: 'user_mark', label: listCol(t, 'username'), showOverflow: true, minWidth: 140, defaultVisible: true },
    { key: 'ip_address', prop: 'system_ip', label: listCol(t, 'ipAddress'), type: 'IP', minWidth: 180, defaultVisible: false },
    { key: 'alert_times', prop: 'alert_times', label: t('securityEvent.colFirstLastAlert'), minWidth: 170, slot: true, defaultVisible: true },
    { key: 'alarm_count', prop: 'alarm_count', label: t('securityEvent.colAlertCount'), minWidth: 90, defaultVisible: true },
    { key: 'handle_status', prop: 'handle_status', label: t('securityEvent.colDisposeStatus'), minWidth: 112, slot: true, showOverflow: true, defaultVisible: true },
    { key: 'handle_result', prop: 'handle_result', label: t('securityEvent.colDisposeResult'), minWidth: 140, slot: true, defaultVisible: true,formatter: (_row,value) => trHandleResultsSummary(t, value) },
    { key: 'attack_tactic', prop: 'attack_tactic', label: t('securityEvent.colAttackStage'), minWidth: 120, slot: true, defaultVisible: true },
  ]
})

// 列设置下拉选项（从 allColumnsConfig 生成）
const columnOptions = computed<ColumnItem[]>(() => {
  return allColumnsConfig.value.map(col => ({
    key: col.key,
    label: col.label,
    defaultVisible: col.defaultVisible,
  }))
})

const visibleColumns = ref<Record<string, boolean>>(
  columnOptions.value.reduce((acc, col) => {
    acc[col.key] = col.defaultVisible ?? true
    return acc
  }, {} as Record<string, boolean>)
)

/** 根据查询条件中的处理状态动态生成批量标记按钮配置 */
const batchMarkButtonConfigs = computed(() => {
  const handleStatus = queryParams.handle_status

  if (handleStatus === SecurityDisposeStatus.Pending) {
    return [
      { text: t('securityEvent.markHandled'), action: 'handled' },
      { text: t('securityEvent.markIgnored'), action: 'ignored' },
    ]
  }

  if (handleStatus === SecurityDisposeStatus.Ignored) {
    return [{ text: t('securityEvent.actionCancelIgnore'), action: 'cancel_ignore' }]
  }

  if (handleStatus === SecurityDisposeStatus.Trusted) {
    return [{ text: t('securityEvent.actionCancelTrust'), action: 'cancel_trust' }]
  }

  return []
})
const cancelIgnoreVisible = ref(false)
const cancelTrustVisible = ref(false)
const markHandledVisible = ref(false)
const markIgnoredVisible = ref(false)
const pendingActionRow = ref<SecurityEventListItem | null>(null)
const pendingActionRows = ref<SecurityEventListItem[]>([])

const dataApi = async (params: SecurityEventListQuery) => {
  const { date: _d, ...rest } = params as SecurityEventListQuery & { date?: unknown }
  return securityEventService.getList(rest)
}

const filterFormColumns = computed(() => {
  void locale.value
  return {
    group_id: {
      slot: true,
      label: listCol(t, 'group'),
      type: FormType.Select,
      colOpt: { style:'flex:0 0 150px' },
      outSearchFiledFlag: true,
    },
    event_level: {
      label: t('securityEvent.colLevel'),
      type: FormType.Select,
      dictData: levelOptions(t),
      selectOpt: { clearable: true },
    },
    event_type: {
      label: t('securityEvent.colEventType'),
      type: FormType.Select,
      dictData: eventTypeOptions(t),
      selectOpt: { clearable: true },
    },
    handle_status: {
      label: t('securityEvent.colDisposeStatus'),
      type: FormType.Select,
      dictData: disposeStatusOptions(t),
      selectOpt: { clearable: true },
    },
    handle_result: {
      label: t('securityEvent.colDisposeResult'),
      type: FormType.Select,
      dictData: handleResultOptions(t),
    },
    attack_tactic: {
      label: t('securityEvent.colAttackStage'),
      type: FormType.Select,
      dictData: attackTacticOptions(t),
      selectOpt: { clearable: true },
    },
    date: {
      label: t('common.timeRange'),
      colOpt:{style:'flex:0 0 260px'},
      type: FormType.Date,
      dateType: DateType.DateRange,
      dateOpt: { 'value-format': 'YYYY-MM-DD', 'start-placeholder': t('portManage.startTime'),
      'end-placeholder': t('portManage.endTime'), clearable: true },
      events: {
        change: (val: [string, string]) => {
          const [s, e] = useParseTime(val)
          queryParams.start_time = s
          queryParams.end_time = e
          void handleQuery()
        },
      },
    },
  } as ObjectFormItem<SecurityEventListQuery>
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  const filteredColumns = allColumnsConfig.value.filter(col => visibleColumns.value[col.key])

  return {
  bodyStyle: props.viewMode === TableViewMode.full ? 'padding: 10px 16px' : 'background: transparent;padding-top:15px',
  height: props.viewMode === TableViewMode.preview ? undefined : '100%',
  disabled: !props.hasPermission,
  path: 'list',
  rowKey: 'business_id',
  selection: true,
  usePagination: false,
  disableScrollLoad: props.viewMode === TableViewMode.preview,
  scrollLoadThreshold: 10,
  scrollLoadDelay: 100,
  showActionBar: false,
  showTip: false,
  computerClick (row: SecurityEventListItem) {
    emit('detail', row)
  },
  columns: filteredColumns,
  }
})

function rowActions (row: SecurityEventListItem) {
  const detail = { name: t('securityEvent.actionDetail'), type: 'detail' }

  const actionsMap: Record<SecurityDisposeStatus, Array<{ name: string; type: string }>> = {
    [SecurityDisposeStatus.Pending]: [
      detail,
      { name: t('securityEvent.actionDispose'), type: 'dispose' },
    ],
    [SecurityDisposeStatus.Handled]: [
      detail,
      { name: t('securityEvent.actionReDispose'), type: 'dispose' },
    ],
    [SecurityDisposeStatus.Ignored]: [
      detail,
      { name: t('securityEvent.actionCancelIgnore'), type: 'cancel_ignore' },
    ],
    [SecurityDisposeStatus.Trusted]: [
      detail,
      { name: t('securityEvent.actionCancelTrust'), type: 'cancel_trust' },
    ],
  }

  return actionsMap[row.handle_status] || [detail]
}

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
  columnOpt: {
    label: listCol(t, 'operation'),
    minWidth: 170,
    fixed: 'right',
    className: 'security-event-list__op-cell',
  },
  actionButtons: (row: SecurityEventListItem) =>
    rowActions(row).map((a) => ({
      name: a.name,
      type: a.type,
      opt: { size: FormSize.Small, text: true, type: 'text' },
    })),
  }
})

/** CmTable 统一 emit：{ type, rowItem } */
function handleAction ({ type, rowItem }: { type: string; rowItem?: SecurityEventListItem }) {
  const row = rowItem
  if (!row) return
  if (type === 'detail') emit('detail', row)
  else if (type === 'dispose') emit('dispose', row)
  else if (type === 'cancel_ignore') {
    pendingActionRow.value = row
    cancelIgnoreVisible.value = true
  } else if (type === 'cancel_trust') {
    pendingActionRow.value = row
    cancelTrustVisible.value = true
  }
}

async function onMarkHandledConfirm () {
  const rows = pendingActionRows.value
  if (!rows.length) return
  await Promise.all(rows.map((r) => securityEventService.markHandled(r.business_id)))
  ElMessage.success(t('common.success'))
  selectedRows.value = []
  pendingActionRows.value = []
  void refreshList()
}

async function onMarkIgnoredConfirm () {
  const rows = pendingActionRows.value
  if (!rows.length) return
  await Promise.all(rows.map((r) => securityEventService.markIgnored(r.business_id)))
  ElMessage.success(t('common.success'))
  selectedRows.value = []
  pendingActionRows.value = []
  void refreshList()
}

async function onCancelIgnoreConfirm () {
  const rows = pendingActionRows.value.length > 0 ? pendingActionRows.value : (pendingActionRow.value ? [pendingActionRow.value] : [])
  if (!rows.length) return
  await securityEventService.cancelIgnore(rows)
  ElMessage.success(t('common.success'))
  selectedRows.value = []
  pendingActionRows.value = []
  void refreshList()
}

async function onCancelTrustConfirm () {
  const rows = pendingActionRows.value.length > 0 ? pendingActionRows.value : (pendingActionRow.value ? [pendingActionRow.value] : [])
  if (!rows.length) return
  await securityEventService.cancelTrust(rows)
  ElMessage.success(t('common.success'))
  selectedRows.value = []
  pendingActionRows.value = []
  void refreshList()
}

function updateGroupId (id: number) {
  if (queryParams.group_id === id) return
  queryParams.group_id = id
  void refreshList()
}

function handleSearch (kw: string) {
  queryParams.keyword = kw
  void refreshList()
}

function handleQuery () {
  void refreshList()
}

function handleReset () {
  const timeInit = resolveInitialTime()
  Object.assign(queryParams, {
    ...buildDefaultListQuery({
      limit: queryParams.limit,
      page: 1,
      handle_status: resolveDefaultHandleStatus(),
      event_level: props.initialEventLevel,
      event_type: props.initialEventType,
      attack_tactic: props.initialAttackTactic,
      start_time: timeInit.start_time,
      end_time: timeInit.end_time,
    }),
    group_id: -1,
    date: timeInit.date,
  })
  void refreshList()
}

async function handleExport (fileType: 'csv' | 'xlsx') {
  try {
    await securityEventService.exportList(queryParams, fileType)
  } catch (e) {
    console.error(e)
    ElMessage.error(t('common.failed'))
  }
}

function onSelectionChange (selection: SecurityEventListItem[] = []) {
  selectedRows.value = selection
}

function onBatchMarkClick (action: string) {
  if (!selectedRows.value.length) {
    ElMessage.warning(t('securityEvent.batchSelectFirst'))
    return
  }

  pendingActionRows.value = [...selectedRows.value]

  const actionHandlers: Record<string, () => void> = {
    handled: () => {
      markHandledVisible.value = true
    },
    ignored: () => {
      markIgnoredVisible.value = true
    },
    cancel_ignore: () => {
      cancelIgnoreVisible.value = true
    },
    cancel_trust: () => {
      cancelTrustVisible.value = true
    },
  }

  const handler = actionHandlers[action]
  if (handler) {
    handler()
  }
}

__expose({ refreshList })

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tag = _resolveComponent("el-tag")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      tableOpt: tableOpt.value,
      tableActOpt: tableActOpt.value,
      "has-permission": __props.hasPermission,
      firstLoading: __props.hasPermission,
      api: dataApi,
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((queryParams) = $event)),
      pageOption: { pageNumName: 'page', pageSizeName: 'limit' },
      onHandleAction: handleAction,
      onChangeSelected: onSelectionChange
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          modelValue: queryParams,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
          "form-columns": filterFormColumns.value,
          disabled: !__props.hasPermission,
          "show-search": true,
          "sync-search-from-field": "keyword",
          "search-placeholder": _ctx.$t('securityEvent.searchPlaceholder'),
          "popover-threshold": 2,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          group_id: _withCtx(() => [
            _createVNode(GroupTreeSelector, {
              disabled: !__props.hasPermission,
              "default-group-id": queryParams.group_id ?? -1,
              "terminal-type": [_unref(TerminalType).windows, _unref(TerminalType).windowsServer],
              onSelected: updateGroupId
            }, null, 8, ["disabled", "default-group-id", "terminal-type"])
          ]),
          actions: _withCtx(() => [
            _createVNode(ColumnSettingsDropdown, {
              modelValue: visibleColumns.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((visibleColumns).value = $event)),
              columns: columnOptions.value,
              disabled: !__props.hasPermission,
              size: _unref(FormSize).Small,
              "button-text": _ctx.$t('common.columnSettings'),
              class: "cm-ui-mr10"
            }, null, 8, ["modelValue", "columns", "disabled", "size", "button-text"]),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(batchMarkButtonConfigs.value, (config) => {
              return _withDirectives((_openBlock(), _createBlock(_component_el_button, {
                key: config.action,
                size: "small",
                disabled: !selectedRows.value.length || !__props.hasPermission,
                onClick: ($event: any) => (onBatchMarkClick(config.action))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(config.text), 1)
                ]),
                _: 2
              }, 1032, ["disabled", "onClick"])), [
                [_vShow, queryParams.handle_status != null]
              ])
            }), 128)),
            _createVNode(ExportDropdown, {
              "has-permission": __props.hasPermission,
              "csv-i18n-key": "securityEvent.exportCsv",
              "excel-i18n-key": "securityEvent.exportExcel",
              onExport: handleExport
            }, null, 8, ["has-permission"]),
            (props.viewMode === _unref(TableViewMode).preview)
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 0,
                  type: "primary",
                  size: "small",
                  plain: "",
                  onClick: handleViewAll,
                  disabled: !__props.hasPermission
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('commonPage.viewAll')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"])
      ]),
      event_level: _withCtx(({ value }) => [
        _createVNode(_component_el_tag, {
          size: "small",
          type: _unref(listLevelColor)(value)
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(trListEventLevel)(_unref(t), value)), 1)
          ]),
          _: 2
        }, 1032, ["type"])
      ]),
      event_id: _withCtx(({ row }) => [
        _createTextVNode(_toDisplayString(_unref(trEventDesc)(_unref(t), row.event_id)), 1)
      ]),
      event_type: _withCtx(({ row }) => [
        _createTextVNode(_toDisplayString(_unref(trEventTypeDisplay)(_unref(t), row)), 1)
      ]),
      alert_times: _withCtx(({ row }) => [
        _createElementVNode("div", null, _toDisplayString(_unref(formatTimeStamp)(row.first_alarm_time)), 1),
        _createElementVNode("div", null, _toDisplayString(_unref(formatTimeStamp)(row.last_alarm_time)), 1)
      ]),
      handle_status: _withCtx(({ value }) => [
        _createVNode(_component_el_tag, {
          size: "small",
          type: _unref(disposeStatusTagType)(value)
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(trDisposeStatus)(_unref(t), value)), 1)
          ]),
          _: 2
        }, 1032, ["type"])
      ]),
      attack_tactic: _withCtx(({ value }) => [
        _createTextVNode(_toDisplayString(_unref(trAttackTactic)(_unref(t), value)), 1)
      ]),
      threat_type: _withCtx(({ row }) => [
        _createTextVNode(_toDisplayString(_unref(trListThreatType)(_unref(t), row)), 1)
      ]),
      _: 1
    }, 8, ["tableOpt", "tableActOpt", "has-permission", "firstLoading", "modelValue"]),
    _createVNode(MarkHandledDialog, {
      modelValue: markHandledVisible.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((markHandledVisible).value = $event)),
      onConfirm: onMarkHandledConfirm
    }, null, 8, ["modelValue"]),
    _createVNode(MarkIgnoredDialog, {
      modelValue: markIgnoredVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((markIgnoredVisible).value = $event)),
      onConfirm: onMarkIgnoredConfirm
    }, null, 8, ["modelValue"]),
    _createVNode(CancelIgnoreDialog, {
      modelValue: cancelIgnoreVisible.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((cancelIgnoreVisible).value = $event)),
      onConfirm: onCancelIgnoreConfirm
    }, null, 8, ["modelValue"]),
    _createVNode(CancelTrustDialog, {
      modelValue: cancelTrustVisible.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((cancelTrustVisible).value = $event)),
      "threat-entity": pendingActionRow.value?.threat_entity,
      onConfirm: onCancelTrustConfirm
    }, null, 8, ["modelValue", "threat-entity"])
  ], 64))
}
}

})